import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, Filter, Download, ArrowLeft, ChevronDown, ChevronUp, Search, Trash2, Clock, TrendingUp, Users, CheckCircle2, AlertCircle, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  adminAuthFetch,
  disableAdminTwoFactor,
  enableAdminTwoFactor,
  getAdminTwoFactorStatus,
  getCurrentAdmin,
  loginAdmin,
  logoutAdmin,
  setupAdminTwoFactor,
} from "../lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API_PREFIX = process.env.REACT_APP_ADMIN_API_PREFIX || (process.env.NODE_ENV === "production" ? "" : "internal-control-7f3a9");
const ADMIN_API_BASE = `${BACKEND_URL}/api${ADMIN_API_PREFIX ? `/${ADMIN_API_PREFIX}` : ""}`;

const interesLabel = {
  campamento: "Campamento", "encuentro-adultos": "Encuentro Adultos",
  cursos: "Extraescolares", contacto: "Contacto", general: "General",
};
const interesColor = {
  campamento: "#2B87BE", "encuentro-adultos": "#2D6A4F",
  cursos: "#C9974A", contacto: "#64748B", general: "#94A3B8",
};
const estadoConfig = {
  pendiente: { label: "Pendiente", color: "#F59E0B", bg: "#FFFBEB" },
  contactado: { label: "Contactado", color: "#3B82F6", bg: "#EFF6FF" },
  confirmado: { label: "Confirmado", color: "#10B981", bg: "#ECFDF5" },
  descartado: { label: "Descartado", color: "#94A3B8", bg: "#F8FAFC" },
};

const fieldLabels = {
  nombre_participante: "Participante", apellidos_participante: "Apellidos participante",
  fecha_nacimiento: "Fecha nacimiento", genero: "Género",
  nombre_contacto: "Contacto", apellidos_contacto: "Apellidos contacto",
  telefono_contacto: "Teléfono", email_contacto: "Email",
  nombre_contacto2: "2º Contacto", apellidos_contacto2: "Apellidos 2º",
  telefono_contacto2: "Tel. 2º", email_contacto2: "Email 2º",
  asistio_antes: "Asistió antes", pautas_medicas: "Pautas médicas",
  alergias: "Alergias", dificultades_comida: "Comida",
  dificultades_dormir: "Dormir", situaciones_dificiles: "Situaciones difíciles",
  actividades_agradables: "Actividades agradables", info_adicional: "Info adicional",
  diagnostico: "Diagnóstico", como_conociste: "Cómo nos conoció",
  para_quien: "Para quién", edad_participante: "Edad participante",
  area_interes: "Área interés", comentarios: "Comentarios",
  nombre: "Nombre", apellidos: "Apellidos", email: "Email", telefono: "Teléfono",
  mensaje: "Mensaje", comunicaciones_comerciales: "Newsletter",
};
const skipFields = ["id", "interes", "fecha", "_id", "estado", "origen"];

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorDisableCode, setTwoFactorDisableCode] = useState("");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [twoFactorMessage, setTwoFactorMessage] = useState("");
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("inscripciones");
  const [leads, setLeads] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalInsc, setTotalInsc] = useState(0);
  const [detailOpen, setDetailOpen] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const logout = useCallback(() => {
    setAuthed(false);
    setEmail("");
    setPassword("");
    setOtp("");
    setAuthError("");
    setTwoFactorStatus(null);
    setTwoFactorSetup(null);
    setTwoFactorCode("");
    setTwoFactorDisableCode("");
    setTwoFactorError("");
    setTwoFactorMessage("");
    setLeads([]);
    setInscripciones([]);
    setStats(null);
    setDetailOpen(null);
    setDetailData(null);
    setLoadError("");
  }, []);

  const refreshTwoFactorStatus = useCallback(async () => {
    const status = await getAdminTwoFactorStatus();
    setTwoFactorStatus(status);
    return status;
  }, []);

  const authorizedFetch = useCallback(async (url, options = {}) => {
    const res = await adminAuthFetch(url, options);

    if (res.status === 401) {
      logout();
      throw new Error("unauthorized");
    }

    if (!res.ok) {
      let detail = "request_failed";
      try {
        const payload = await res.json();
        detail = payload?.detail || detail;
      } catch {
        // Ignore non-JSON error payloads.
      }
      const error = new Error(detail);
      error.status = res.status;
      throw error;
    }

    return res;
  }, [logout]);

  const fetchLeads = useCallback(async () => {
    const params = new URLSearchParams({ limit: "200" });
    if (filter) params.set("interes", filter);
    if (searchDebounced) params.set("search", searchDebounced);
    const res = await authorizedFetch(`${ADMIN_API_BASE}/leads?${params}`);
    const data = await res.json();
    setLeads(Array.isArray(data.leads) ? data.leads : []);
    setTotalLeads(typeof data.total === "number" ? data.total : 0);
  }, [authorizedFetch, filter, searchDebounced]);

  const fetchInscripciones = useCallback(async () => {
    const params = new URLSearchParams({ limit: "200" });
    if (filter) params.set("interes", filter);
    if (searchDebounced) params.set("search", searchDebounced);
    const res = await authorizedFetch(`${ADMIN_API_BASE}/inscripciones?${params}`);
    const data = await res.json();
    setInscripciones(Array.isArray(data.inscripciones) ? data.inscripciones : []);
    setTotalInsc(typeof data.total === "number" ? data.total : 0);
  }, [authorizedFetch, filter, searchDebounced]);

  const fetchStats = useCallback(async () => {
    const res = await authorizedFetch(`${ADMIN_API_BASE}/stats`);
    setStats(await res.json());
  }, [authorizedFetch]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");

    try {
      await loginAdmin(email, password, otp);
      setAuthed(true);
      setPassword("");
      setOtp("");
      await refreshTwoFactorStatus();
    } catch (error) {
      setAuthed(false);
      if (error.status === 429) {
        setAuthError("Demasiados intentos. Espera y vuelve a probar.");
      } else if (error.message === "LOGIN_2FA_REQUIRED") {
        setAuthError("Introduce el codigo 2FA de tu app autenticadora.");
      } else if (error.message === "LOGIN_BAD_OTP") {
        setAuthError("El codigo 2FA no es valido.");
      } else if (error.message === "LOGIN_BAD_CREDENTIALS") {
        setAuthError("Credenciales incorrectas");
      } else {
        setAuthError("No se pudo iniciar sesión");
      }
    } finally {
      setAuthLoading(false);
    }
  }, [email, otp, password, refreshTwoFactorStatus]);

  useEffect(() => {
    let ignore = false;

    const validateSession = async () => {
      try {
        const user = await getCurrentAdmin();
        if (!ignore) {
          if (user) {
            setAuthed(true);
            await refreshTwoFactorStatus();
          } else {
            setAuthed(false);
          }
        }
      } catch {
        if (!ignore) {
          setAuthed(false);
          setAuthError("No se pudo validar la sesión");
        }
      } finally {
        if (!ignore) {
          setSessionChecking(false);
        }
      }
    };

    validateSession();
    return () => {
      ignore = true;
    };
  }, [refreshTwoFactorStatus]);

  const handleLogout = useCallback(async () => {
    try {
      await logoutAdmin();
    } finally {
      logout();
    }
  }, [logout]);

  const handleStartTwoFactorSetup = useCallback(async () => {
    setTwoFactorLoading(true);
    setTwoFactorError("");
    setTwoFactorMessage("");
    try {
      const setup = await setupAdminTwoFactor();
      setTwoFactorSetup(setup);
    } catch {
      setTwoFactorError("No se pudo iniciar la configuracion del 2FA.");
    } finally {
      setTwoFactorLoading(false);
    }
  }, []);

  const handleEnableTwoFactor = useCallback(async (e) => {
    e.preventDefault();
    setTwoFactorLoading(true);
    setTwoFactorError("");
    setTwoFactorMessage("");
    try {
      await enableAdminTwoFactor(twoFactorCode);
      await refreshTwoFactorStatus();
      setTwoFactorMessage("2FA activado. El proximo login ya pedira codigo.");
      setTwoFactorSetup(null);
      setTwoFactorCode("");
    } catch (error) {
      if (error.message === "LOGIN_BAD_OTP") {
        setTwoFactorError("El codigo no es valido. Revisa la app autenticadora.");
      } else {
        setTwoFactorError("No se pudo activar el 2FA.");
      }
    } finally {
      setTwoFactorLoading(false);
    }
  }, [refreshTwoFactorStatus, twoFactorCode]);

  const handleDisableTwoFactor = useCallback(async (e) => {
    e.preventDefault();
    setTwoFactorLoading(true);
    setTwoFactorError("");
    setTwoFactorMessage("");
    try {
      await disableAdminTwoFactor(twoFactorDisableCode);
      await refreshTwoFactorStatus();
      setTwoFactorDisableCode("");
      setTwoFactorSetup(null);
      setTwoFactorMessage("2FA desactivado.");
    } catch (error) {
      if (error.message === "LOGIN_BAD_OTP") {
        setTwoFactorError("El codigo no es valido.");
      } else {
        setTwoFactorError("No se pudo desactivar el 2FA.");
      }
    } finally {
      setTwoFactorLoading(false);
    }
  }, [refreshTwoFactorStatus, twoFactorDisableCode]);

  useEffect(() => {
    if (!authed || sessionChecking) return;
    const loadAdminData = async () => {
      setLoadError("");
      try {
        await fetchStats();
        if (tab === "inscripciones") await fetchInscripciones();
        else await fetchLeads();
      } catch {
        setLoadError("No se pudieron cargar los datos del panel. Revisa el backend o la ruta privada del admin.");
      }
    };

    loadAdminData();
  }, [authed, fetchInscripciones, fetchLeads, fetchStats, sessionChecking, tab]);

  const openDetail = async (id) => {
    if (detailOpen === id) { setDetailOpen(null); setDetailData(null); return; }
    const res = await authorizedFetch(`${ADMIN_API_BASE}/inscripciones/${id}`);
    setDetailData(await res.json());
    setDetailOpen(id);
  };

  const updateEstado = async (id, estado, collection) => {
    await authorizedFetch(`${ADMIN_API_BASE}/${collection}/${id}/estado`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
    if (collection === "inscripciones") fetchInscripciones();
    else fetchLeads();
    fetchStats();
  };

  const deleteItem = async (id, collection) => {
    await authorizedFetch(`${ADMIN_API_BASE}/${collection}/${id}`, { method: "DELETE" });
    setConfirmDelete(null);
    if (collection === "inscripciones") fetchInscripciones();
    else fetchLeads();
    fetchStats();
  };

  const exportCSV = () => {
    const items = tab === "inscripciones" ? inscripciones : leads;
    if (!items.length) return;
    const keys = Object.keys(items[0]).filter(k => k !== "_id");
    const header = keys.join(",");
    const rows = items.map(item => keys.map(k => `"${String(item[k] || "").replace(/"/g, '""')}"`).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `${tab}_dacpac_${new Date().toISOString().split("T")[0]}.csv`; a.click();
  };

  if (sessionChecking) {
    return (
      <main style={{ fontFamily: "Manrope, sans-serif" }} className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-sm text-slate-500">Validando sesion...</p>
      </main>
    );
  }

  if (!authed) {
    return (
      <main style={{ fontFamily: "Manrope, sans-serif" }} className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="admin-login">
        <div className="w-full max-w-sm px-5">
          <div className="text-center mb-8">
            <h1 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl font-bold mb-2">Panel Admin</h1>
            <p className="text-slate-500 text-sm">Acceso restringido</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-username-input"
              className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm" />
            <input type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
              className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm" />
            <input type="text" inputMode="numeric" placeholder="Codigo 2FA (si esta activado)" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm tracking-[0.3em]" />
            {authError ? <p className="text-sm text-red-600">{authError}</p> : null}
            <button type="submit" data-testid="admin-login-btn" disabled={authLoading || !email || !password} className="btn-primary w-full justify-center py-4 disabled:opacity-60">
              {authLoading ? "Validando..." : "Acceder"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main style={{ fontFamily: "Manrope, sans-serif" }} className="min-h-screen bg-[#F1F5F9]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-16">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-3"><ArrowLeft size={14} /> Volver</Link>
            <h1 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl font-bold">Panel de Administración</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCSV} data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 transition-colors" style={{ color: "#0D3A4F" }}>
              <Download size={14} /> Exportar CSV
            </button>
            <button onClick={handleLogout}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 transition-colors" style={{ color: "#0D3A4F" }}>
              Salir
            </button>
          </div>
        </div>

        <div className="mb-8 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Seguridad admin</h2>
              <p className="mt-2 text-sm text-slate-600">
                {twoFactorStatus?.enabled ? "2FA activo para este usuario administrador." : "2FA desactivado. Activalo para exigir codigo en cada login."}
              </p>
              {twoFactorMessage ? <p className="mt-2 text-sm text-green-600">{twoFactorMessage}</p> : null}
              {twoFactorError ? <p className="mt-2 text-sm text-red-600">{twoFactorError}</p> : null}
            </div>
            {!twoFactorStatus?.enabled ? (
              <button
                onClick={handleStartTwoFactorSetup}
                disabled={twoFactorLoading}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-[#0D3A4F] hover:bg-slate-50 disabled:opacity-60"
              >
                {twoFactorLoading ? "Preparando..." : "Configurar 2FA"}
              </button>
            ) : null}
          </div>

          {twoFactorSetup ? (
            <form onSubmit={handleEnableTwoFactor} className="mt-5 grid gap-4 rounded-2xl bg-slate-50 p-5">
              <div className="flex justify-center">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <QRCodeSVG value={twoFactorSetup.otpauth_uri} size={180} includeMargin />
                </div>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Secreto</div>
                <div className="break-all rounded-xl border border-slate-200 bg-white px-4 py-3 font-mono text-sm text-slate-700">{twoFactorSetup.secret}</div>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">URI para app autenticadora</div>
                <div className="break-all rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">{twoFactorSetup.otpauth_uri}</div>
              </div>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Introduce el codigo de 6 digitos"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm tracking-[0.3em] focus:border-[#1B5F7E] focus:outline-none focus:ring-2 focus:ring-[#1B5F7E]/10"
              />
              <div className="flex gap-3">
                <button type="submit" disabled={twoFactorLoading || twoFactorCode.length !== 6} className="btn-primary justify-center px-5 py-3 disabled:opacity-60">
                  {twoFactorLoading ? "Activando..." : "Activar 2FA"}
                </button>
                <button type="button" onClick={() => { setTwoFactorSetup(null); setTwoFactorCode(""); setTwoFactorError(""); setTwoFactorMessage(""); }}
                  className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
              </div>
            </form>
          ) : null}

          {twoFactorStatus?.enabled ? (
            <form onSubmit={handleDisableTwoFactor} className="mt-5 flex flex-col gap-3 rounded-2xl bg-slate-50 p-5 lg:flex-row lg:items-center">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Codigo actual para desactivar"
                value={twoFactorDisableCode}
                onChange={(e) => setTwoFactorDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm tracking-[0.3em] focus:border-[#1B5F7E] focus:outline-none focus:ring-2 focus:ring-[#1B5F7E]/10 lg:max-w-xs"
              />
              <button type="submit" disabled={twoFactorLoading || twoFactorDisableCode.length !== 6}
                className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                {twoFactorLoading ? "Procesando..." : "Desactivar 2FA"}
              </button>
            </form>
          ) : null}
        </div>

        {/* Summary cards */}
        {loadError ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {loadError}
          </div>
        ) : null}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3"><Users size={16} style={{ color: "#1B5F7E" }} /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total registros</span></div>
              <div className="text-3xl font-bold" style={{ color: "#0D3A4F" }}>{stats.total}</div>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3"><TrendingUp size={16} style={{ color: "#10B981" }} /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Nuevos esta semana</span></div>
              <div className="text-3xl font-bold" style={{ color: "#10B981" }}>{stats.nuevos_semana}</div>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3"><AlertCircle size={16} style={{ color: "#F59E0B" }} /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Pendientes</span></div>
              <div className="text-3xl font-bold" style={{ color: "#F59E0B" }}>{stats.pendientes}</div>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={16} style={{ color: "#3B82F6" }} /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Inscripciones</span></div>
              <div className="text-3xl font-bold" style={{ color: "#3B82F6" }}>{stats.total_inscripciones}</div>
            </div>
          </div>
        )}

        {/* Stats by category + Chart */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            {/* By interes */}
            <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Por categoría</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_interes || {}).map(([key, count]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: interesColor[key] || "#94A3B8" }} />
                      <span className="text-sm text-slate-600">{interesLabel[key] || key}</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: "#0D3A4F" }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Timeline chart */}
            <div className="lg:col-span-2 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Actividad (últimas 8 semanas)</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stats.timeline || []} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="semana" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="inscripciones" name="Inscripciones" fill="#2B87BE" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="contactos" name="Contactos" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Tabs + Search */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex gap-3">
            {[{ key: "inscripciones", label: "Inscripciones" }, { key: "leads", label: "Contactos / Newsletter" }].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setFilter(""); setSearch(""); }} data-testid={`tab-${t.key}`}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{ background: tab === t.key ? "#0D3A4F" : "white", color: tab === t.key ? "white" : "#64748B", border: `1px solid ${tab === t.key ? "#0D3A4F" : "#E2E8F0"}` }}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text" placeholder="Buscar por nombre o email..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Filter size={14} className="text-slate-400" />
          <div className="flex gap-2 flex-wrap">
            {(tab === "inscripciones"
              ? [{ value: "", label: "Todos" }, { value: "campamento", label: "Campamento" }, { value: "encuentro-adultos", label: "Adultos" }, { value: "cursos", label: "Extraescolares" }]
              : [{ value: "", label: "Todos" }, { value: "contacto", label: "Contacto" }]
            ).map((f) => (
              <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`filter-${f.value || "all"}`}
                className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
                style={{ background: filter === f.value ? "#0D3A4F" : "white", color: filter === f.value ? "white" : "#64748B", border: `1px solid ${filter === f.value ? "#0D3A4F" : "#E2E8F0"}` }}>
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400 ml-auto">{tab === "inscripciones" ? totalInsc : totalLeads} resultados</span>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {tab === "inscripciones" ? (
            <div>
              {inscripciones.length === 0 ? (
                <div className="px-5 py-16 text-center text-slate-400">No hay inscripciones registradas</div>
              ) : inscripciones.map((item, i) => {
                const estado = item.estado || "pendiente";
                const ec = estadoConfig[estado] || estadoConfig.pendiente;
                const name = item.nombre_participante
                  ? `${item.nombre_participante} ${item.apellidos_participante || ""}`
                  : `${item.nombre || ""} ${item.apellidos || ""}`;
                const email = item.email_contacto || item.email || "";
                return (
                  <div key={item.id || i} className="border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => openDetail(item.id)} data-testid={`insc-row-${i}`}>
                      <div className="flex-1 flex items-center gap-4 min-w-0">
                        <span className="text-xs text-slate-400 whitespace-nowrap w-20 flex-shrink-0">
                          {new Date(item.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                        </span>
                        <span className="font-semibold text-sm truncate" style={{ color: "#0D3A4F" }}>{name}</span>
                        <span className="text-xs text-slate-400 truncate hidden md:block">{email}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold"
                          style={{ background: `${interesColor[item.interes] || "#94A3B8"}15`, color: interesColor[item.interes] || "#94A3B8" }}>
                          {interesLabel[item.interes] || item.interes}
                        </span>
                        {/* Estado selector */}
                        <select
                          value={estado}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { e.stopPropagation(); updateEstado(item.id, e.target.value, "inscripciones"); }}
                          data-testid={`estado-select-${i}`}
                          className="text-[10px] font-bold px-2 py-1 rounded-full border-0 cursor-pointer"
                          style={{ background: ec.bg, color: ec.color }}
                        >
                          {Object.entries(estadoConfig).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                        <button onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: item.id, name, col: "inscripciones" }); }} data-testid={`delete-btn-${i}`}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 size={13} />
                        </button>
                        {detailOpen === item.id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>
                    </div>
                    <AnimatePresence>
                      {detailOpen === item.id && detailData && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <div className="px-5 pb-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5 rounded-xl bg-slate-50">
                              {Object.entries(detailData).filter(([k, v]) => !skipFields.includes(k) && v && v !== "").map(([k, v]) => (
                                <div key={k} className={String(v).length > 60 ? "md:col-span-2" : ""}>
                                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{fieldLabels[k] || k}</div>
                                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{typeof v === "boolean" ? (v ? "Sí" : "No") : String(v)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Fecha</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Nombre</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Email</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Teléfono</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Mensaje</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Newsletter</th>
                    <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Estado</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => {
                    const estado = lead.estado || "pendiente";
                    const ec = estadoConfig[estado] || estadoConfig.pendiente;
                    return (
                      <tr key={lead.id || i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-4 text-slate-400 text-xs whitespace-nowrap">{new Date(lead.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}</td>
                        <td className="px-5 py-4 font-semibold text-sm" style={{ color: "#0D3A4F" }}>{lead.nombre} {lead.apellidos || ""}</td>
                        <td className="px-5 py-4 text-slate-600 text-xs"><a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a></td>
                        <td className="px-5 py-4 text-slate-500 text-xs">{lead.telefono || "—"}</td>
                        <td className="px-5 py-4 text-slate-500 text-xs max-w-[160px] truncate">{lead.mensaje || "—"}</td>
                        <td className="px-5 py-4 text-xs">{lead.comunicaciones_comerciales ? <span className="text-green-600 font-bold">Sí</span> : <span className="text-slate-300">No</span>}</td>
                        <td className="px-5 py-4">
                          <select value={estado} onChange={(e) => updateEstado(lead.id, e.target.value, "leads")} data-testid={`lead-estado-${i}`}
                            className="text-[10px] font-bold px-2 py-1 rounded-full border-0 cursor-pointer"
                            style={{ background: ec.bg, color: ec.color }}>
                            {Object.entries(estadoConfig).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={() => setConfirmDelete({ id: lead.id, name: `${lead.nombre} ${lead.apellidos || ""}`, col: "leads" })} data-testid={`lead-delete-${i}`}
                            className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                  {leads.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-400">No hay contactos registrados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5"
            onClick={() => setConfirmDelete(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-2" style={{ color: "#0D3A4F" }}>Eliminar registro</h3>
              <p className="text-sm text-slate-500 mb-6">¿Seguro que quieres eliminar a <strong>{confirmDelete.name}</strong>? Esta acción no se puede deshacer.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700">Cancelar</button>
                <button onClick={() => deleteItem(confirmDelete.id, confirmDelete.col)} data-testid="confirm-delete-btn"
                  className="px-5 py-2 text-sm font-semibold text-white rounded-xl" style={{ background: "#EF4444" }}>Eliminar</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
